import { gql } from '@apollo/client';
import {
  InvoiceDocumentsFields,
  InvoiceIdFields,
  InvoiceStatusFields,
  TraceFields,
} from './fragments';

export const UPLOAD_INVOICE_SIGNATURE = gql`
  mutation uploadInvoiceSignature(
    $invoiceIds: [Int]!
    $annexUploadFile: Upload!
    $paybackUploadFile: Upload!
  ) {
    uploadInvoiceSignature(
      invoiceIds: $invoiceIds
      annexUploadFile: $annexUploadFile
      paybackUploadFile: $paybackUploadFile
    ) {
      invoices {
        id
        status
      }
    }
  }
`;

export const UPLOAD_FACTORING_INVOICES = gql`
  mutation uploadFactoringInvoices($companyId: ID!, $file: Upload!, $currency: String!) {
    uploadFactoringInvoices(companyId: $companyId, file: $file, currency: $currency) {
      success
      rosterId
    }
  }
`;

export const LOAD_NEW_INVOICES = gql`
  mutation LoadNewInvoices {
    loadNewInvoices {
      taskId
    }
  }
`;

export const FILE_URL = gql`
  mutation invoicePdfUrl($invoiceId: Int!) {
    invoicePdfUrl(invoiceId: $invoiceId) {
      invoice {
        id
        documents {
          ...InvoiceDocumentsFields
        }
      }
    }
  }
  ${InvoiceDocumentsFields}
`;

export const LOAD_OFFERS_MISSING_XML = gql`
  mutation loadOffersMissingXml($invoiceIds: [Int]!) {
    loadOffersMissingXml(invoiceIds: $invoiceIds) {
      success
    }
  }
`;

export const CREATE_OPERATION = gql`
  mutation CreateOperation(
    $assignmentRequests: [AssignmentRequestType]
    $bankAccountId: Int
    $isExternal: Boolean!
    $companyId: Int!
    $digitalCertificatePassword: String
    $requestingPlatformCode: String
  ) {
    createOperation(
      assignmentRequests: $assignmentRequests
      bankAccountId: $bankAccountId
      isExternal: $isExternal
      companyId: $companyId
      digitalCertificatePassword: $digitalCertificatePassword
      requestingPlatformCode: $requestingPlatformCode
    ) {
      response
      showNps
    }
  }
`;

export const CREATE_REINTEGRATION = gql`
  mutation CreateReintegration(
    $owedInvoicesIds: [Int]!
    $payingInvoicesIds: [Int]!
  ) {
    createReintegration(
      owedInvoicesIds: $owedInvoicesIds
      payingInvoicesIds: $payingInvoicesIds
    ) {
      success
    }
  }
`;

export const CREATE_REPLACEMENTS = gql`
  mutation CreateReplacements(
    $replacedInvoiceIds: [Int]!
    $replacingInvoiceIds: [Int]!
    $useSurplusForDifference: Boolean
  ) {
    createReplacements(
      replacedInvoiceIds: $replacedInvoiceIds
      replacingInvoiceIds: $replacingInvoiceIds
      useSurplusForDifference: $useSurplusForDifference
    ) {
      replacements {
        id
      }
    }
  }
`;

export const FETCH_MANY_TRACE = gql`
  mutation fetchManyTrace($invoiceId: [Int]!) {
    fetchManyTrace(invoiceId: $invoiceId) {
      invoices {
        ...InvoiceIdFields
        ...InvoiceStatusFields
        traces {
          ...TraceFields
        }
      }
    }
  }
  ${InvoiceIdFields}
  ${InvoiceStatusFields}
  ${TraceFields}
`;

export const ASSIGN_EVALUATOR = gql`
  mutation assignEvaluator(
    $companyId: Int!
    $modelName: String!
    $assign: Boolean!
    $userId: Int
  ) {
    assignEvaluator(
      companyId: $companyId
      modelName: $modelName
      assign: $assign
      userId: $userId
    ) {
      success
    }
  }
`;

export const REEXECUTE_INVOICE_ACTION = gql`
  mutation reexecuteInvoiceAction(
    $invoicesIds: [String]!
    $reexecutionType: String!
    $reason: String!
    $comments: String
    $rut: String
  ) {
    reexecuteInvoiceAction(
      invoicesIds: $invoicesIds
      reexecutionType: $reexecutionType
      reason: $reason
      comments: $comments
      rut: $rut
    ) {
      success
    }
  }
`;

export const ADD_OR_EDIT_WHITELIST = gql`
  mutation AddOrEditWhitelist(
    $emitterRut: String
    $receiverRut: String
    $whitelistId: Int
    $monthlyRate: Float
    $defaultRate: Float
    $retentionRate: Float
    $active: Boolean
    $whitelistType: String
    $monthlyRateMethod: String!
    $defaultRateMethod: String!
    $commissionMethod: String!
    $spread: Float
    $commissionUfAmount: Float
  ) {
    addOrEditRateWhitelist(
      emitterRut: $emitterRut
      receiverRut: $receiverRut
      whitelistId: $whitelistId
      monthlyRate: $monthlyRate
      defaultRate: $defaultRate
      retentionRate: $retentionRate
      active: $active
      whitelistType: $whitelistType
      monthlyRateMethod: $monthlyRateMethod
      defaultRateMethod: $defaultRateMethod
      commissionMethod: $commissionMethod
      spread: $spread
      commissionUfAmount: $commissionUfAmount
    ) {
      rateWhitelist {
        id
      }
    }
  }
`;

export const TOGGLE_RATE_WHITELIST_ACTIVE_STATUS = gql`
  mutation ToggleRateWhitelistActiveStatus($whitelistId: Int!) {
    toggleRateWhitelistActiveStatus(whitelistId: $whitelistId) {
      rateWhitelist {
        id
        active
      }
    }
  }
`;

export const ADD_OR_EDIT_TERM_WHITELIST = gql`
  mutation AddOrEditTermWhitelist(
    $whitelistId: Int
    $paymentDays: Int
    $changeActive: Boolean
    $receiverId: Int
    $companyId: Int
  ) {
    addOrEditTermWhitelist(
      whitelistId: $whitelistId
      paymentDays: $paymentDays
      changeActive: $changeActive
      receiverId: $receiverId
      companyId: $companyId
    ) {
      termWhitelist {
        id
      }
    }
  }
`;

export const CREATE_TERM_WHITELISTS_FROM_FILE = gql`
  mutation CreateTermWhitelistsFromFile($file: Upload!) {
    createTermWhitelistsFromFile(file: $file) {
      success
      message
      totalRows
      successfulCreations
      skippedRows
      errors
    }
  }
`;

export const GENERATE_PENDING_SIGNATURE_CONTRACT_ANNEX = gql`
  mutation createPendingSignatureContractAnnex(
    $signatureDate: Date!
    $companyId: String!
    $companyContractName: String!
    $invoiceIds: [String]!
    $bankAccountId: String!
    $legalRepresentatives: [CreatePendingSignatureContractAnnexPersonInput]!
    $endorsements: [CreatePendingSignatureContractAnnexPersonInput]
    $publicDeedNumber: String!
    $publicDeedDate: Date!
    $notaryName: String!
    $notaryNumber: String!
    $notaryAddress: String!
  ) {
    createPendingSignatureContractAnnex(
      signatureDate: $signatureDate
      companyId: $companyId
      companyContractName: $companyContractName
      invoiceIds: $invoiceIds
      bankAccountId: $bankAccountId
      legalRepresentatives: $legalRepresentatives
      endorsements: $endorsements
      publicDeedNumber: $publicDeedNumber
      publicDeedDate: $publicDeedDate
      notaryName: $notaryName
      notaryNumber: $notaryNumber
      notaryAddress: $notaryAddress
    ) {
      contractAnnex {
        id
        globalAppId
        createdAt
        lastFile
        paybackFile
      }
    }
  }
`;

export const DELETE_RELATED_ANNEX = gql`
  mutation DeleteRelatedAnnex($annexId: Int!) {
    deletePendingSignatureContractAnnex(annexId: $annexId) {
      response
    }
  }
`;

export const GENERATE_CLIENT_EVALUATION_PDF = gql`
  mutation generateClientEvaluationPdf($masterEntityId: Int!) {
    generateClientEvaluationPdf(masterEntityId: $masterEntityId) {
      success
      url 
    }
  }
`;
